import { useRef, useState } from 'react';
import clsx from 'clsx';
import { Check, Plus, Upload, X } from '@phosphor-icons/react';
import { useEpisodeStore } from '@/state/episodeStore';
import { useSelectionStore } from '@/state/selectionStore';
import { MediaImage } from '@/components/MediaImage';
import { createCharacter } from '@/services/charactersApi';
import { toast } from '@/state/toastStore';
import type { Character } from '@/types';

export function CastTab() {
  const characters = useEpisodeStore((s) => s.characters);
  const setBundle = useEpisodeStore.getState().setBundle;
  const episode = useEpisodeStore((s) => s.episode);
  const scenes = useEpisodeStore((s) => s.scenes);
  const images = useEpisodeStore((s) => s.images);
  const videos = useEpisodeStore((s) => s.videos);
  const activeCastIds = useSelectionStore((s) => s.pendingReferences.castIds);
  const toggleCast = useSelectionStore((s) => s.toggleReferenceCast);

  const [adding, setAdding] = useState(false);

  function onAdded(c: Character) {
    setAdding(false);
    if (!episode) return;
    setBundle({
      episode,
      characters: [...characters, c],
      scenes,
      images: Object.values(images),
      videos: Object.values(videos),
    });
    toast.ok(`Added ${c.name}.`);
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-text-3">
          {characters.length} {characters.length === 1 ? 'member' : 'members'}
          {activeCastIds.length > 0 && (
            <span className="ml-2 text-accent">
              · {activeCastIds.length} active
            </span>
          )}
        </p>
        <button
          type="button"
          onClick={() => setAdding(true)}
          aria-label="Add character"
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] font-medium text-text-2 transition-colors hover:bg-surface-2 hover:text-text-1"
        >
          <Plus size={14} />
          Add
        </button>
      </div>

      {adding && episode && (
        <AddCastForm
          episodeUuid={episode.uuid}
          onCancel={() => setAdding(false)}
          onCreated={onAdded}
        />
      )}

      {characters.length === 0 && !adding && (
        <p className="text-[13px] text-text-4">
          Add cast members so the model knows who's in the story.
        </p>
      )}

      <ul className="space-y-1">
        {characters.map((c) => (
          <CharacterRow
            key={c.id}
            character={c}
            episodeUuid={episode?.uuid ?? ''}
            isActive={activeCastIds.includes(c.id)}
            onToggle={() => toggleCast(c.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function CharacterRow({
  character,
  episodeUuid,
  isActive,
  onToggle,
}: {
  character: Character;
  episodeUuid: string;
  isActive: boolean;
  onToggle: () => void;
}) {
  const tone = TYPE_TONE[character.type];
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        className={clsx(
          'flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors',
          isActive ? 'bg-accent-soft' : 'hover:bg-surface-2',
        )}
      >
        <Avatar character={character} episodeUuid={episodeUuid} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-medium text-text-1">{character.name}</p>
          <p className={clsx('text-[11px] uppercase tracking-wide', tone)}>
            {character.type}
          </p>
        </div>
        <Checkbox checked={isActive} />
      </button>
    </li>
  );
}

function AddCastForm({
  episodeUuid,
  onCancel,
  onCreated,
}: {
  episodeUuid: string;
  onCancel: () => void;
  onCreated: (c: Character) => void;
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState<Character['type']>('character');
  const [description, setDescription] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function onPickFile(file: File) {
    const url = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    setPreview(url);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const c = await createCharacter(episodeUuid, {
        name: name.trim(),
        type,
        description: description.trim() || undefined,
        imageDataUrl: preview ?? undefined,
      });
      onCreated(c);
    } catch (err) {
      toast.err(err instanceof Error ? err.message : 'Add failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="mb-4 space-y-3 rounded-md border border-line-1 bg-surface-2 p-3"
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-text-3">
          New cast member
        </p>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="rounded-md p-1 text-text-3 hover:bg-surface-3 hover:text-text-1"
        >
          <X size={12} />
        </button>
      </div>

      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name"
        autoFocus
        className="w-full rounded-md bg-surface-inset px-3 py-2 text-[13px] text-text-1 placeholder:text-text-4 outline-none ring-1 ring-line-2 focus:ring-line-3"
      />

      <div className="flex gap-2">
        {(['character', 'prop', 'location', 'other'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setType(t)}
            className={clsx(
              'rounded-md px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide transition-colors',
              t === type
                ? 'bg-accent-soft text-accent'
                : 'bg-surface-3 text-text-3 hover:text-text-1',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        placeholder="Short description (optional)"
        className="w-full resize-none rounded-md bg-surface-inset px-3 py-2 text-[13px] text-text-1 placeholder:text-text-4 outline-none ring-1 ring-line-2 focus:ring-line-3"
      />

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onPickFile(f);
          e.target.value = '';
        }}
      />

      {preview ? (
        <div className="flex items-center gap-3">
          <img
            src={preview}
            alt=""
            className="h-12 w-12 rounded-md object-cover"
          />
          <button
            type="button"
            onClick={() => setPreview(null)}
            className="text-[11px] text-text-3 hover:text-err"
          >
            Remove image
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 rounded-md bg-surface-3 px-3 py-2 text-[12px] font-medium text-text-2 transition-colors hover:text-text-1"
        >
          <Upload size={12} />
          Upload image
        </button>
      )}

      <button
        type="submit"
        disabled={submitting || !name.trim()}
        className="w-full rounded-md bg-accent px-3 py-2 text-[13px] font-medium text-text-on-accent transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-surface-3 disabled:text-text-4"
      >
        {submitting ? 'Adding…' : 'Add cast member'}
      </button>
    </form>
  );
}

function Checkbox({ checked }: { checked: boolean }) {
  return (
    <span
      className={clsx(
        'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border-2 transition-colors',
        checked
          ? 'border-accent bg-accent text-text-on-accent'
          : 'border-text-4 bg-transparent',
      )}
    >
      {checked && <Check size={10} weight="bold" />}
    </span>
  );
}

function Avatar({
  character,
  episodeUuid,
}: {
  character: Character;
  episodeUuid: string;
}) {
  if (character.imagePath) {
    return (
      <MediaImage
        episodeUuid={episodeUuid}
        path={character.imagePath}
        className="h-10 w-10 shrink-0 rounded-md object-cover"
      />
    );
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-soft font-mono text-[12px] font-semibold text-text-1">
      {initials(character.name)}
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

const TYPE_TONE: Record<Character['type'], string> = {
  character: 'text-info',
  prop: 'text-warn',
  location: 'text-ok',
  other: 'text-text-3',
};
